import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, MapPin, Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const StoreLocations = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    location_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    contact_person: '',
    contact_phone: '',
    is_primary: false,
    location_type: 'store',
    operating_hours: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const data = await apiCall('/api/store-locations');
      setLocations(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load store locations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await apiCall(`/api/store-locations/${editingLocation.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        toast({ title: 'Success', description: 'Location updated successfully' });
      } else {
        await apiCall('/api/store-locations', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        toast({ title: 'Success', description: 'Location created successfully' });
      }
      fetchLocations();
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save location',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      location_name: location.location_name,
      address: location.address,
      city: location.city,
      state: location.state,
      pincode: location.pincode,
      country: location.country,
      contact_person: location.contact_person || '',
      contact_phone: location.contact_phone || '',
      is_primary: location.is_primary,
      location_type: location.location_type || 'store',
      operating_hours: location.operating_hours || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    
    try {
      await apiCall(`/api/store-locations/${locationId}`, { method: 'DELETE' });
      toast({ title: 'Success', description: 'Location deleted successfully' });
      fetchLocations();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete location',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      location_name: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      contact_person: '',
      contact_phone: '',
      is_primary: false,
      location_type: 'store',
      operating_hours: ''
    });
    setEditingLocation(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Loading locations...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Store Locations</h1>
            <p className="text-gray-600 mt-2">Manage your business locations</p>
          </div>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
        </div>

        {locations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Locations Yet</h3>
              <p className="text-gray-600 mb-4">Add your first business location to get started</p>
              <Button onClick={() => setShowModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location) => (
              <Card key={location.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-blue-500" />
                        {location.location_name}
                        {location.is_primary && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </CardTitle>
                      <Badge className="mt-2" variant="outline">
                        {location.location_type}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(location)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(location.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">{location.address}</p>
                    <p className="text-gray-600">
                      {location.city}, {location.state} - {location.pincode}
                    </p>
                    {location.contact_person && (
                      <div className="pt-2 border-t">
                        <p className="text-gray-700 font-medium">{location.contact_person}</p>
                        {location.contact_phone && (
                          <p className="text-gray-600">{location.contact_phone}</p>
                        )}
                      </div>
                    )}
                    {location.operating_hours && (
                      <div className="pt-2">
                        <p className="text-gray-600 text-xs">{location.operating_hours}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-4">
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="location_name">Location Name</Label>
                      <Input
                        id="location_name"
                        value={formData.location_name}
                        onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                        placeholder="e.g., Main Store, Warehouse 1"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Street address"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="pincode">Pincode</Label>
                      <Input
                        id="pincode"
                        value={formData.pincode}
                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="location_type">Location Type</Label>
                      <select
                        id="location_type"
                        value={formData.location_type}
                        onChange={(e) => setFormData({ ...formData, location_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="store">Store</option>
                        <option value="warehouse">Warehouse</option>
                        <option value="office">Office</option>
                        <option value="service_center">Service Center</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="contact_person">Contact Person</Label>
                      <Input
                        id="contact_person"
                        value={formData.contact_person}
                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone">Contact Phone</Label>
                      <Input
                        id="contact_phone"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="operating_hours">Operating Hours</Label>
                      <Input
                        id="operating_hours"
                        value={formData.operating_hours}
                        onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
                        placeholder="e.g., Mon-Fri: 9AM-6PM"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_primary"
                      checked={formData.is_primary}
                      onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_primary">Set as Primary Location</Label>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingLocation ? 'Update Location' : 'Add Location'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StoreLocations;
